'use client'

import {
  fbTracking,
  sendSegmentCheckFitment,
  sendSegmentProductViewed,
  trackGoogleRemarketing,
} from '../utils/trackingEvents'
import ProductPageWiring from './ProductPageWiring'

import { useContext, useEffect, useState } from 'react'

import { RecommendationProvider } from 'Clutch/Stores/RecommendationStore/RecommendationStore'
import { useUserStore } from 'Clutch/Stores/UserStore/UserStore'

import { GetBrowserUserValues } from 'Clutch/Utilities/CookieUtilties/CookieReader'
import createZustandContext from 'Utilities/Zustand/createZustandContext'

import { useUserSpecificProductDataStore } from 'Clutch/Contexts/UserSpecificProductDataStore/UserSpecificProductDataStore'
import { TrackingContext } from 'Contexts/TrackingContext/TrackingContext'

import useFacebook from 'Clutch/Hooks/useFacebook'
import useNetwork from 'Clutch/Hooks/useNetwork'
import useRecentHistory from 'Clutch/Hooks/useRecentHistory'
import useSegment from 'Hooks/useSegment'

const ProductPageContext = createZustandContext(
  ({ set, get, initialProps, props }) => {
    const initialState = {
      ...props.value,
      quantityRequested: 1,
      isIndexOptionErrorState: false,
      isDetailsAccordionOpen: true,
      isCarbAccordionOpen: false,
      isSpecsAccordionOpen: true,
      userToggledSpecsAccordion: false,
      isRatingsAndReviewsAccordionOpen: false,
      isPowerReviewsSlidesOpen: false,
      isQuestionsAndAnswersAccordionOpen: false,
      isVideosAndArticlesAccordionOpen: false,
      loadingFreeShippingAndAvailability: true,
      loadingShippingEstimate: true,
      loadingIndexOptionSelection: false,
      aPlusContentScrollToLock: { active: false },
      readyForScrollData: { Details: false },
      isFitmentDrawerOpen: false,
      isAdditionalFacetDrawerOpen: false,
      isAdapterDrawerOpen: false,
      isGarageSaleDrawerOpen: false,
      isCompareToolPresent: true,
      shippingInView: false,
      checkFitmentEventSent: false,
      isNextLinkLoading: false,
      isFitmentAccordionOpen: false,
    }

    return {
      ...initialProps,
      ...initialState,

      bustStaticPage: (sendFacebookEvent, skipReporting) => {
        import('./fetches/product.js').then((f) => {
          f.bustStaticPage(get().productPageId, get().skuVariantNumber, get()._dependencies.webUserGuid).then(
            (data) => {
              if (!data || data.should404) {
                // Avoid overwriting ssr data with a "empty" response, which can lead to an infinite loop
                console.error('Failed to bust static page, received empty response or 404. Not updating store.')
                return
              }

              set((x) => {
                // WIFR-33209 There has to be a better way to do this but this works for now
                const returnObj = {
                  ...x,
                  ...data,
                  isLazyDataHydrated: true,
                }

                return returnObj
              })

              // WIFR-33209 This is so PDP events don't conflict with our counts for Snowflake
              if (!get().isConfigurator) {
                sendSegmentProductViewed(
                  data,
                  get()._dependencies.setMisoId,
                  get()._dependencies.sendSegmentTrackEventWithName,
                )
              }

              trackGoogleRemarketing(data, get().retailLowPrice, get().reportingSkuVariantNumber)

              if (!GetBrowserUserValues().isGpcEnabled) {
                fbTracking(sendFacebookEvent, get().skuVariantNumber, get().title)
              }
            },
          )
        })
      },

      errorIndexOptions: () => {
        set({ isIndexOptionErrorState: true })
        const element = document.getElementById('pdp_offer')
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      },

      updateQuantityRequested: (quantity) => {
        get().updatePriceWithPriceBreak(quantity)
        set({ quantityRequested: parseInt(quantity) })
      },

      updatePriceWithPriceBreak: (quantityRequested) => {
        try {
          const { quantityBreaks = [], originalPrice, price, originalSavingsPrice, savingsPrice } = get()

          if (!quantityBreaks.length) {
            set({ needToCalculateQuantityBreak: false })
            return
          }

          const basePrice = originalPrice?.length ? originalPrice : price
          const baseSavingsPrice = originalSavingsPrice?.length ? originalSavingsPrice : savingsPrice

          const quantityBreak = quantityBreaks.findLast(
            ({ priceLevelQuantity }) => quantityRequested >= priceLevelQuantity,
          )

          const newPrice = quantityBreak?.price || basePrice
          const newSavingsPrice = quantityBreak?.savingsPrice || baseSavingsPrice

          set({
            originalPrice: basePrice,
            originalSavingsPrice: baseSavingsPrice,
            price: newPrice,
            savingsPrice: newSavingsPrice,
            needToCalculateQuantityBreak: false,
          })
        } catch (error) {
          console.error('Failed to update price', error)
        }
      },

      selectIndexOption: async (
        productPageId,
        skuVariant,
        selectedIndexOptions,
        mostRecentlySelectedIndexOption,
        sendFacebookEvent,
      ) => {
        import('./fetches/product.js').then((f) => {
          set({ loadingIndexOptionSelection: true })
          f.onIndexOptionSelection(
            productPageId,
            skuVariant,
            selectedIndexOptions,
            mostRecentlySelectedIndexOption,
            get()._dependencies.webUserGuid,
          ).then((data) => {
            if (!data) {
              console.error('Failed to select index option, received empty response. Not updating store.')
              set({ loadingIndexOptionSelection: false, isIndexOptionErrorState: true })
              return
            }

            set((state) => ({
              ...state,
              ...data,
              originalPrice: 0,
              originalSavingsPrice: 0,
              needToCalculateQuantityBreak: true,
              loadingIndexOptionSelection: false,
            }))

            if (data.productPageUrl) {
              // Selecting a variant swaps content in place, so keep the customer where they were -
              // they are usually reading the options or specs, not the top of the page.
              window.history.replaceState(null, '', `${data.productPageUrl}`)
            }

            useRecentHistory.AddToRecentHistory({
              skuBaseNumber: data.skuBaseNumber,
              url: data.productPageUrl,
              title: data.title,
              brand: data.brand?.brandName,
              segmentCategorization: data.segmentCategorization,
            })

            sendSegmentProductViewed(
              data,
              get()._dependencies.setMisoId,
              get()._dependencies.sendSegmentTrackEventWithName,
            )
            trackGoogleRemarketing(data, get().retailLowPrice, get().reportingSkuVariantNumber)
            fbTracking(sendFacebookEvent, get().skuVariantNumber, get().title)

            if (get().shippingInView) {
              get().getFreeShippingAndAvailability(data.skuVariantNumber)
              get().getActiveInStorePickupLocations()
            }

            get().getLastPurchasedDateForSku(get()._dependencies.webUserGuid, data.skuVariantNumber)
          })
        })
      },

      updatePricesForPromo: (prices) => {
        set((state) => {
          return {
            ...state,
            ...prices,
          }
        })
      },

      setPromoInContext: (promo) => {
        set((state) => {
          return {
            ...state,
            promoPreview: promo,
          }
        })
      },

      getFreeShippingAndAvailability: (skuVariant) => {
        const productPageId = get().productPageId
        import('./fetches/product.js').then((f) => {
          f.loadFreeShippingAndAvailability(productPageId, skuVariant).then((data) => {
            if (data && data.freeShippingAndAvailability) {
              set({
                loadingFreeShippingAndAvailability: false,
                freeShippingAndAvailability: data.freeShippingAndAvailability,
              })
            }
          })
        })
      },

      scrollTo: (id, accordionName = '', elementInAccordion = false, shouldOpenAccordion = true) => {
        set({
          aPlusContentScrollToLock: {
            active: true,
            id: id,
            accordionName: accordionName,
            waitForAccordionOpen: elementInAccordion,
          },
          shouldOpenAccordion: shouldOpenAccordion,
        })
      },

      toggleAccordion: (accordion, isOpen) => set({ [`is${accordion}AccordionOpen`]: isOpen }),

      setIsReadyForScrollData: (state, accordionName) =>
        set({
          readyForScrollData: {
            ...get().readyForScrollData,
            [accordionName]: state,
          },
        }),

      getLastPurchasedDateForSku: (webUserGuid, skuVariant) => {
        import('./fetches/checkout.js').then((f) => {
          f.getPurchasedDateForSkuAsync(webUserGuid, skuVariant).then((data) => {
            set({
              lastPurchaseDateForSku: data && data.purchasedDateForSku ? data.purchasedDateForSku.orderedDate : null,
            })
          })
        })
      },

      getActiveInStorePickupLocations: () => {
        import('./fetches/checkout.js').then((f) => {
          f.getActiveInStorePickupLocations().then((data) => {
            set({
              activeInStorePickupLocations:
                data && data.allActiveInStorePickupLocationByMonth ? data.allActiveInStorePickupLocationByMonth : [],
            })
          })
        })
      },

      checkFitment: (checkFitmentRequest) => {
        if (checkFitmentRequest && checkFitmentRequest.some((x) => x.engineIds && x.engineIds.manufacturerId > 0)) {
          console.log('New Engine Interaction')
        }
        import('./fetches/product.js').then((f) => {
          f.checkFitmentNew(get().skuBaseNumber, get().skuVariantNumber, checkFitmentRequest).then((data) => {
            if (data && data.checkFitment) {
              if (data.checkFitment?.fitment?.some((x) => x.value?.unverifiedEngineUsed)) {
                if (!get().checkFitmentEventSent) {
                  console.log('Unverified Engine Used')
                  set({ checkFitmentEventSent: true })
                  const fitment = data.checkFitment.fitment.filter((x) => x.value?.unverifiedEngineUsed)?.[0]
                  const request = checkFitmentRequest.find((x) => x.requestIdentifier === fitment.key)
                  const skuData = {
                    skuBaseNumber: get().skuBaseNumber,
                    skuVariantNumber: get().skuVariantNumber,
                  }
                  sendSegmentCheckFitment(
                    request?.engineIds,
                    skuData,
                    get()._dependencies.sendSegmentTrackEventWithName,
                  )
                }
              }
            }
            set((state) => {
              return {
                ...state,
                ...data.checkFitment,
              }
            })
          })
        })
      },

      setShippingEstimate: (skuVariant, postalCode) => {
        if (get()._dependencies.isBot()) {
          set({ loadingShippingEstimate: false })
          return
        }

        const shippingEstimate = get()._dependencies.shippingDataBySkuBasesOrVariants[skuVariant]

        set({
          loadingShippingEstimate: false,
          shippingEstimate: {
            postalCode: postalCode,
            ...shippingEstimate,
          },
        })
      },

      setUpsellOptions: (upsellOptions) => {
        set((state) => {
          return {
            ...state,
            upsellOptions: [...upsellOptions],
          }
        })
      },

      setCouldOfferUpsells: (couldOfferUpsells) => {
        set((state) => {
          return {
            ...state,
            couldOfferUpsells: couldOfferUpsells,
          }
        })
      },

      loadMoreReviews: (nextPageUrl) => {
        import('./fetches/powerReviews').then((f) => {
          f.loadMoreReviews(nextPageUrl).then((data) => {
            if (data) {
              set((state) => ({
                ...state,
                powerReviewsResponse: {
                  ...state.powerReviewsResponse,
                  paging: { ...data.paging },
                  results: [...state.powerReviewsResponse.results, ...data.results],
                },
              }))
            }
          })
        })
      },

      loadFilteredReviews: (ratingStars, sortOrder, searchTerm) => {
        import('./fetches/powerReviews').then((f) => {
          f.getFilteredReviews(
            ratingStars,
            sortOrder,
            searchTerm,
            get().powerReviewsMerchantId,
            get().skuBaseNumber,
            get().powerReviewsApiKey,
          ).then((data) => {
            if (data) {
              set((state) => ({
                ...state,
                powerReviewsResponse: {
                  ...state.powerReviewsResponse,
                  paging: { ...data.paging },
                  results: [...data.results],
                },
              }))
            }
          })
        })
      },

      loadMoreQuestions: (nextPageUrl) => {
        import('./fetches/powerReviews').then((f) => {
          f.loadMoreQuestions(nextPageUrl).then((data) => {
            if (data) {
              set((state) => ({
                ...state,
                powerReviewsQnAResponse: {
                  ...state.powerReviewsQnAResponse,
                  paging: { ...data.paging },
                  results: [...state.powerReviewsQnAResponse.results, ...data.results],
                },
              }))
            }
          })
        })
      },

      loadFilteredQuestions: (sortOrder) => {
        import('./fetches/powerReviews').then((f) => {
          f.getFilteredQuestions(
            sortOrder,
            get().powerReviewsMerchantId,
            get().skuBaseNumber,
            get().powerReviewsApiKey,
          ).then((data) => {
            if (data) {
              set((state) => ({
                ...state,
                powerReviewsQnAResponse: {
                  ...state.powerReviewsQnAResponse,
                  paging: { ...data.paging },
                  results: [...data.results],
                },
              }))
            }
          })
        })
      },

      setShippingInView: (value) => set({ shippingInView: value }),

      setIsPowerReviewsSlidesOpen: (value) => set({ isPowerReviewsSlidesOpen: value }),

      setIsCompareToolPresent: (value) => set({ isCompareToolPresent: value }),

      showFitmentDrawer: () => set({ isFitmentDrawerOpen: true }),
      hideFitmentDrawer: () => set({ isFitmentDrawerOpen: false }),

      showAdditionalFacetDrawer: () => set({ isAdditionalFacetDrawerOpen: true }),
      hideAdditionalFacetDrawer: () => set({ isAdditionalFacetDrawerOpen: false }),

      showGarageSaleDrawer: () => set({ isGarageSaleDrawerOpen: true }),
      hideGarageSaleDrawer: () => set({ isGarageSaleDrawerOpen: false }),

      showWhatsInTheKitDrawer: () => set({ isWhatsInTheKitDrawerOpen: true }),
      hideWhatsInTheKitDrawer: () => set({ isWhatsInTheKitDrawerOpen: false }),

      showInStorePickupDrawer: () => set({ isInStorePickupDrawerOpen: true }),
      hideInStorePickupDrawer: () => set({ isInStorePickupDrawerOpen: false }),

      showAdapterDrawer: () => set({ isAdapterDrawerOpen: true }),
      hideAdapterDrawer: () => set({ isAdapterDrawerOpen: false }),

      setProductPageId: (productPageId) => set({ productPageId }),
      setSkuBaseNumber: (skuBaseNumber) => set({ skuBaseNumber }),
      setSkuVariantNumber: (skuVariantNumber) => set({ skuVariantNumber }),
      productPageStoreSet: (object) => set(object),
      productPageStoreReplace: (newState) =>
        set(() => ({
          ...newState,
          isLazyDataHydrated: false,
          _dependencies: get()._dependencies, // Preserve dependencies
        })),

      startNextLinkNavigation: () => {
        import('../utils/iosSafeScrollToTop.js').then(({ scrollToTopForNextLink }) => {
          scrollToTopForNextLink()
        })
        set({ isNextLinkLoading: true })
      },

      scrollToTopAndStartNavigation: () => {
        import('../utils/iosSafeScrollToTop.js').then(({ scrollToTopForNextLink }) => {
          scrollToTopForNextLink()
        })
        set({ isNextLinkLoading: true })
      },

      completeNextLinkNavigation: () => {
        set({ isNextLinkLoading: false })
      },
    }
  },
  {
    name: 'PdpStore',
  },
)

ProductPageContext.useSynchronizedState = () => {
  const isBot = useUserStore((x) => x.context.isBot)
  const webUserGuid = useUserStore((x) => x.context.userGuid)
  const userVin = useUserStore((x) => x.context.userVin)
  const { sendSegmentTrackEventWithName } = useSegment()
  const { setMisoId } = useContext(TrackingContext)
  const shippingDataBySkuBasesOrVariants = useUserSpecificProductDataStore((x) => x.shippingDataBySkuBasesOrVariants)

  return {
    _dependencies: {
      isBot,
      webUserGuid,
      userVin,
      sendSegmentTrackEventWithName,
      setMisoId,
      shippingDataBySkuBasesOrVariants,
    },
  }
}

ProductPageContext.Wiring = ProductPageWiring

const ProductPageProviderComponent = ({ children, value }) => {
  return (
    <ProductPageContext.Provider value={value}>
      <StateSynchronizer value={value}>{children}</StateSynchronizer>
    </ProductPageContext.Provider>
  )
}

const StateSynchronizer = ({ children, value }) => {
  const sendFacebookEvent = useFacebook()

  const productPageStoreReplace = useProductPageStore((x) => x.productPageStoreReplace)
  const bustStaticPage = useProductPageStore((x) => x.bustStaticPage)
  const completeNextLinkNavigation = useProductPageStore((x) => x.completeNextLinkNavigation)
  const isNextLinkLoading = useProductPageStore((x) => x.isNextLinkLoading)
  const currentProductPageId = useProductPageStore((x) => x.productPageId)

  // Subscribe to store changes to update RecommendationProvider
  const storeTitle = useProductPageStore((x) => x.title)
  const storeSkuBaseNumber = useProductPageStore((x) => x.skuBaseNumber)
  const storeSkuVariantNumber = useProductPageStore((x) => x.skuVariantNumber)

  const [nextLinksFollowed, setNextLinksFollowed] = useState(0)

  // this use effect should only be relevant on NextLink navigations
  useEffect(() => {
    if (isNextLinkLoading && value) {
      productPageStoreReplace(value)
      bustStaticPage(sendFacebookEvent)
      completeNextLinkNavigation()
      setNextLinksFollowed((prev) => prev + 1)
    } else if (value && value.productPageId && value.productPageId !== currentProductPageId) {
      productPageStoreReplace(value)
      bustStaticPage(sendFacebookEvent)
    }
  }, [value, currentProductPageId])

  useNetwork(
    (data) => {
      if (nextLinksFollowed > 0) {
        analytics.page({
          speed: `${data.speed}`,
          network: `${data.network}`,
          cpu: `${data.cpu}`,
          memory: `${data.memory}`,
          dataSaver: `${data.dataSaver}`,
        })
      }
    },
    [nextLinksFollowed],
  )

  return (
    <RecommendationProvider
      sections={['PDP_Top', 'PDP_Aside', 'PDP_Bottom', 'PDP_Kits', 'PDP_Articles']}
      keywords={storeTitle}
      skuBaseNumbers={storeSkuBaseNumber ? [storeSkuBaseNumber] : []}
      skuVariantNumbers={storeSkuVariantNumber ? [storeSkuVariantNumber] : []}
    >
      {children}
    </RecommendationProvider>
  )
}

export const useProductPageStore = ProductPageContext.useStore
export const ProductPageProvider = ProductPageProviderComponent
